/**
 * What to know before this concept - the lesson plan's prerequisites.
 *
 * Shared by the mind map's concept panel and the Knowledge Archive sidebar.
 * Each item either leads somewhere (`onOpen`: another concept of the lesson,
 * or one of the student's Mindspace concepts) or is plain text marked "Not
 * studied yet" - a prerequisite the student has no lesson on. A button with
 * no destination would be a dead end, so an item without `onOpen` is not a
 * button.
 */

import { ArrowUpRight, Milestone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InlineMarkdown } from '@/components/core/markdown/markdown';

export interface PrerequisiteItem {
  name: string;
  /** Present when the prerequisite is one of the student's concepts. */
  onOpen?: () => void;
}

export function PrerequisitesBox({
  items,
  className,
}: {
  items: PrerequisiteItem[];
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <section
      aria-label='Know first'
      className={cn(
        'rounded-lg border border-border bg-muted/40 px-3 py-2.5',
        className
      )}
    >
      <h4 className='mb-1.5 flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-muted-foreground'>
        <Milestone className='h-3.5 w-3.5' aria-hidden />
        Know first
      </h4>
      <ul className='space-y-0.5'>
        {items.map(item => (
          <li key={item.name}>
            {item.onOpen ? (
              <button
                type='button'
                onClick={item.onOpen}
                className='group flex w-full items-start gap-1 rounded-md px-1 py-0.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted max-sm:min-h-11 max-sm:items-center'
              >
                <InlineMarkdown className='min-w-0 flex-1'>
                  {item.name}
                </InlineMarkdown>
                <ArrowUpRight
                  className='mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground'
                  aria-hidden
                />
              </button>
            ) : (
              <p className='px-1 py-0.5 text-sm text-foreground'>
                <InlineMarkdown>{item.name}</InlineMarkdown>
                <span className='block text-xs text-muted-foreground'>
                  Not studied yet
                </span>
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
